import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import Box from "@mui/material/Box";
import saveAs from "file-saver";
import JSZip from "jszip";
import { enqueueSnackbar } from "notistack";
import { ReactElement, useState } from "react";

import { Asset } from "../../../generated/gql/graphql";
import useText from "../../../hooks/use-text";
import {
  getDirectusAssetsPath,
  useDirectus,
} from "../../../providers/DirectusProvider";
import AssetUtils from "../../../services/asset";
import { LocalizedStrings, SupportedLanguages } from "../../../utils/lang";

type Props = {
  assets: Asset[];
  open: boolean;
  onClose(): void;
};

export default function DownloadAssets(props: Props): ReactElement {
  const { assets, open, onClose } = props;

  const [isLoading, setIsLoading] = useState(false);

  // Todo(john): don't use directly the directus sdk to download the asses
  const { directus } = useDirectus();
  const { strings } = useText(local);

  async function downloadAssets(): Promise<void> {
    setIsLoading(true);

    try {
      const zip = new JSZip();

      const calls = assets.map(async (asset, idx) => {
        if (asset.image) {
          const { type } = (await AssetUtils(directus).getFileType(
            asset.image
          )) as { type: string };
          const imageUrl = `${getDirectusAssetsPath(directus)}/${
            asset.image
          }?download`;

          const imageBlob = await fetch(imageUrl).then((response) =>
            response.blob()
          );

          zip.file(
            `${idx}-${asset.name}.${type?.substring(type?.indexOf("/") + 1)}`,
            imageBlob,
            { binary: true }
          );
        }
      });

      await Promise.all(calls);

      zip
        .generateAsync({ type: "blob" })
        .then((content) => saveAs(content, "metahub-archive"));
    } catch (err) {
      console.error(err);
      enqueueSnackbar(strings.error, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {strings.title.replace("{{count}}", assets.length.toString())}
      </DialogTitle>
      <DialogContent className="min-w-[300px]">
        <ul className="max-h-40 space-y-5 bg-gray-100 p-5">
          {assets.map((asset: Asset) => (
            <li key={asset.id}>
              <p className="text-sm">{asset.name}</p>
            </li>
          ))}
        </ul>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{strings.cancel}</Button>
        <Button onClick={downloadAssets}>
          {isLoading ? (
            <Box className="flex justify-center">
              <CircularProgress size="1rem" className="w-1 h-1" />
            </Box>
          ) : (
            strings.download
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

type LocalSchema = {
  title: string;
  cancel: string;
  download: string;
  error: string;
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    title: "Download Assets ({{count}})",
    cancel: "Cancel",
    download: "Download",
    error: "Failed to download assets. Please try it again",
  },
};
